import React, { useState, useEffect } from 'react';
import './App.css';
import MapView from './components/MapView';
import StudentProfiles from './components/StudentProfiles';
import Analytics from './components/Analytics';
import { loadData, filterStudents, filterStudentsByLocation } from './utils/dataUtils';
import { devLog, devError } from './utils/secureLogger';
import Sidebar from './components/Sidebar';
import Papa from 'papaparse';
import Shortlist from './components/Shortlist';
import LoadingAnimation from './components/LoadingAnimation';
import ApplicantRegistrationForm from './ApplicantRegistrationForm';
import RecruiterRegistrationForm from './RecruiterRegistrationForm';
import AdminApproveRecruiters from './AdminApproveRecruiters';
import LoginForm from './LoginForm';
import { nacmeLogo } from './assets/images';
import { getValidToken, API_URL } from './utils/authUtils';
import AdminRouteGuard from './components/AdminRouteGuard';
import { ADMIN_SECTIONS } from './utils/adminPermissions';

// Helper function to extract area code from phone number
const getAreaCodeFromPhone = (phone) => {
  if (!phone) {
    devLog('No phone number provided');
    return null;
  }
  
  // Remove any non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Check if we have enough digits for an area code
  if (digits.length < 3) {
    devLog(`Phone number too short: ${phone}`);
    return null;
  }
  
  // Extract first 3 digits as area code
  const areaCode = digits.substring(0, 3);
  return areaCode;
};

// Calculate distance between two points using Haversine formula
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3958.8; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

const App = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [activeTab, setActiveTab] = useState('map');
  const [filters, setFilters] = useState({
    preferredInstitutions: [],
    majors: [],
    minGpa: 3.0,
    years: [],
    skills: []
  });
  const [zipCode, setZipCode] = useState('');
  const [radiusMiles, setRadiusMiles] = useState(50);
  const [centerZip, setCenterZip] = useState('');
  const [zipCodeMap, setZipCodeMap] = useState(new Map());
  const [areaCodeMap, setAreaCodeMap] = useState(new Map());
  const [shortlist, setShortlist] = useState(() => {
    const saved = localStorage.getItem('shortlist');
    return saved ? JSON.parse(saved) : [];
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    localStorage.setItem('shortlist', JSON.stringify(shortlist));
  }, [shortlist]);

  useEffect(() => {
    devLog('App component mounted');
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get auth token for API calls
        const token = getValidToken();
        if (!token) {
          throw new Error('Authentication required to load data');
        }

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        devLog('Loading data from protected API endpoints...');

        // Load area code coordinates from API
        devLog('Loading area code data...');
        const areaCodeResponse = await fetch(`${API_URL}/api/data/area-codes`, { headers });
        if (!areaCodeResponse.ok) {
          throw new Error(`Failed to fetch area code data: ${areaCodeResponse.status}`);
        }

        const areaCodeJson = await areaCodeResponse.json();
        const areaCodeData = areaCodeJson.data;
        
        if (!areaCodeData || areaCodeData.length === 0) {
          throw new Error('No data found in area code CSV');
        }

        devLog(`Found ${areaCodeData.length} rows in area code data`);

        const newAreaCodeMap = new Map();
        let validEntries = 0;
        let invalidEntries = 0;

        areaCodeData.forEach((row) => {
          if (row['Area Code'] && row.Latitude && row.Longitude) {
            const areaCode = row['Area Code'].trim();
            const latitude = parseFloat(row.Latitude.trim());
            const longitude = parseFloat(row.Longitude.trim());

            if (!isNaN(latitude) && !isNaN(longitude)) {
              newAreaCodeMap.set(areaCode, {
                latitude,
                longitude
              });
              validEntries++;
            } else {
              devError(`Invalid coordinates for area code ${areaCode}: lat=${row.Latitude}, lon=${row.Longitude}`);
              invalidEntries++;
            }
          } else {
            devError(`Missing data in area code row: ${JSON.stringify(row)}`);
            invalidEntries++;
          }
        });

        // Add missing area codes with approximate coordinates
        const missingAreaCodes = {
          '430': { latitude: 32.7767, longitude: -96.7970 }, // Dallas, TX
          '934': { latitude: 40.7128, longitude: -74.0060 }, // New York, NY
          '012': { latitude: 42.3601, longitude: -71.0589 }, // Boston, MA
          '906': { latitude: 46.4917, longitude: -84.3333 }, // Upper Peninsula, MI
          '475': { latitude: 41.3083, longitude: -72.9279 }, // New Haven, CT
          '470': { latitude: 33.7490, longitude: -84.3880 }, // Atlanta, GA
          '787': { latitude: 18.4655, longitude: -66.1057 }, // San Juan, PR
          '939': { latitude: 18.4655, longitude: -66.1057 }, // Puerto Rico
          '984': { latitude: 35.7796, longitude: -78.6382 }, // Raleigh, NC
          '986': { latitude: 43.6150, longitude: -116.2023 }, // Boise, ID
          '989': { latitude: 43.6150, longitude: -116.2023 }, // Michigan
          '994': { latitude: 33.7490, longitude: -84.3880 }, // Georgia
          '995': { latitude: 61.2181, longitude: -149.9003 }, // Alaska
          '996': { latitude: 61.2181, longitude: -149.9003 }, // Alaska
          '997': { latitude: 61.2181, longitude: -149.9003 }, // Alaska
          '998': { latitude: 61.2181, longitude: -149.9003 }, // Alaska
          '999': { latitude: 61.2181, longitude: -149.9003 }  // Alaska
        };

        Object.entries(missingAreaCodes).forEach(([code, coords]) => {
          newAreaCodeMap.set(code, coords);
          devLog(`Added missing area code ${code} with coordinates: ${JSON.stringify(coords)}`);
        });

        devLog(`Loaded ${validEntries} valid area code coordinates`);
        devLog(`Skipped ${invalidEntries} invalid area code entries`);
        devLog(`Total area codes in map: ${newAreaCodeMap.size}`);

        // Log a sample of the area codes to verify data
        const sampleAreaCodes = Array.from(newAreaCodeMap.keys()).slice(0, 5);
        devLog('Sample area codes:');
        sampleAreaCodes.forEach(code => {
          const coords = newAreaCodeMap.get(code);
          devLog(`Area code ${code}: ${JSON.stringify(coords)}`);
        });

        setAreaCodeMap(newAreaCodeMap);

        // Load zip code coordinates from API
        const zipCodeResponse = await fetch(`${API_URL}/api/data/zip-codes`, { headers });
        if (!zipCodeResponse.ok) {
          throw new Error(`Failed to fetch zip code data: ${zipCodeResponse.status}`);
        }

        const zipCodeJson = await zipCodeResponse.json();
        const zipCodeData = zipCodeJson.data;

        if (!zipCodeData || zipCodeData.length === 0) {
          throw new Error('No data found in zip code CSV');
        }

        const newZipCodeMap = new Map();
        zipCodeData.forEach((row) => {
          if (row.ZIP && row['Latitude '] && row.Longitude) {
            newZipCodeMap.set(row.ZIP, {
              latitude: parseFloat(row['Latitude ']),
              longitude: parseFloat(row.Longitude)
            });
          }
        });
        devLog(`Loaded ${newZipCodeMap.size} zip code coordinates`);
        setZipCodeMap(newZipCodeMap);

        // Load student data from API
        devLog('Loading student data...');
        const studentResponse = await fetch(`${API_URL}/api/data/students`, { headers });
        if (!studentResponse.ok) {
          throw new Error(`Failed to fetch student data: ${studentResponse.status}`);
        }

        const studentJson = await studentResponse.json();
        const rawStudentData = studentJson.data;
        
        if (!rawStudentData || rawStudentData.length === 0) {
          throw new Error('No student data found');
        }

        devLog(`Loaded ${rawStudentData.length} raw student records from API`);
        
        // Process raw CSV data into structured student objects
        const processedStudents = rawStudentData.map(row => {
          const areaCode = getAreaCodeFromPhone(row.Phone);
          const location = areaCode ? newAreaCodeMap.get(areaCode) : null;

          return {
            name: row.Name,
            major: row['University/College Major'],
            gpa: parseFloat(row['University/College GPA']) || 0,
            year: row['Academic Class'],
            skills: [],
            phone: row.Phone,
            Internal_ID: row['Internal ID'],
            latitude: location?.latitude,
            longitude: location?.longitude,
            university: row['University/College'],
            email: row.email || ''
          };
        });

        devLog(`Processed ${processedStudents.length} student records`);
        
        // Debug: Log sample student data and unique values
        if (processedStudents.length > 0) {
          devLog('Sample student:', JSON.stringify(processedStudents[0], null, 2));
          
          const majors = processedStudents.map(s => s.major).filter(m => m && m.trim());
          const years = processedStudents.map(s => s.year).filter(y => y && y.trim());
          
          devLog(`Unique majors found: ${Array.from(new Set(majors)).length}`);
          devLog(`Unique years found: ${Array.from(new Set(years)).length}`);
          devLog(`Sample majors:`, Array.from(new Set(majors)).slice(0, 5));
          devLog(`Sample years:`, Array.from(new Set(years)).slice(0, 5));
        }
        
        setStudents(processedStudents);
        setFilteredStudents(processedStudents);

        devLog('Data fetch completed successfully');
      } catch (error) {
        devError(`Error loading data: ${error}`);
        setError(error instanceof Error ? error.message : 'An error occurred while loading data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Apply filters whenever filters, students, or location change
  useEffect(() => {
    if (students.length > 0) {
      let filtered = filterStudents(students, filters);
      
      // Apply location filter if zip code and radius are provided
      if (centerZip && radiusMiles) {
        filterStudentsByLocation(filtered, centerZip, radiusMiles, areaCodeMap)
          .then(locationFiltered => {
            setFilteredStudents(locationFiltered);
            devLog(`Applied location filter: ${locationFiltered.length} students within ${radiusMiles} miles of ${centerZip}`);
          })
          .catch(error => {
            devError(`Location filtering error: ${error}`);
            setFilteredStudents(filtered);
          });
      } else {
        setFilteredStudents(filtered);
        devLog(`Applied filters: ${filtered.length} students match current filters`);
      }
    }
  }, [students, filters, centerZip, radiusMiles, areaCodeMap]);

  const handleLoginSuccess = (token) => {
    localStorage.setItem('token', token);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUserRole(null);
    setActiveTab('map'); // Reset to default tab
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const handleLocationFilter = (zip, radius) => {
    setCenterZip(zip);
    setRadiusMiles(radius);
  };

  const handleStudentSelect = (student) => {
    // Your existing student selection logic
  };

  const addToShortlist = (student) => {
    setShortlist(prev => {
      // Check if student is already in shortlist
      if (prev.some(s => s.Internal_ID === student.Internal_ID)) {
        return prev;
      }
      return [...prev, student];
    });
  };

  const removeFromShortlist = (student) => {
    setShortlist(prev => prev.filter(s => s.Internal_ID !== student.Internal_ID));
  };

  if (isLoading) {
    return <LoadingAnimation onComplete={() => setIsLoading(false)} />;
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-screen text-red-500 p-4 text-center">
        <h2 className="text-2xl font-bold mb-4">Data Loading Error</h2>
        <p className="mb-4">{error}</p>
        <div className="bg-red-100 p-4 rounded-lg max-w-md">
          <h3 className="font-bold mb-2">Possible Solutions:</h3>
          <ul className="list-disc list-inside text-left text-red-700">
            <li>Verify students.csv is in the public/ directory</li>
            <li>Verify us-area-code-geo.csv is in the public/ directory</li>
            <li>Verify full_us_zip_to_latlon.csv is in the public/ directory</li>
            <li>Check file permissions</li>
            <li>Ensure CSV files are not empty</li>
            <li>Check browser console for detailed error</li>
          </ul>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry Loading
        </button>
      </div>
    );
  }

  return (
    <AdminRouteGuard requiredPermission={ADMIN_SECTIONS.MAP_VIEW_ACCESS}>
      <div className={`flex h-screen ${darkMode ? 'dark bg-gray-900 text-white' : 'bg-white text-black'}`}>
        <Sidebar
          students={students}
          filters={filters}
          setFilters={setFilters}
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
          onLocationFilter={handleLocationFilter}
        />
        <main className={`flex-grow overflow-auto ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
          <div className="flex items-center justify-between px-4 pt-8 pb-2 mb-8">
            <div className="flex items-center space-x-6">
              <img
                src={nacmeLogo}
                alt="NACME Logo"
                className="h-10 w-auto drop-shadow"
                style={{ minWidth: 40 }}
              />
              <span
                className="font-bold text-3xl md:text-4xl"
                style={{
                  fontFamily: "'Poppins', 'Segoe UI', 'Helvetica Neue', Arial, 'sans-serif'",
                  textShadow: "0 2px 8px rgba(0,0,0,0.10), 0 1.5px 2px rgba(0,0,0,0.10)"
                }}
              >
                Searchlight
              </span>
            </div>
            <span
              className="ml-4 px-4 py-1 rounded-full bg-[#f3f4f6] text-[#00A0DF] font-semibold text-xs shadow"
              style={{
                fontFamily: "'Poppins', 'Segoe UI', 'Helvetica Neue', Arial, 'sans-serif'",
                letterSpacing: "0.05em"
              }}
            >
              Dashboard v2.0
            </span>
          </div>

          <div className="flex border-b">
            {[
              { label: 'Map View', value: 'map' },
              { label: 'Student Profiles', value: 'students' },
              { label: 'Analytics', value: 'analytics' },
              { label: 'Shortlist', value: 'shortlist' }
            ].map(tab => (
              <button
                key={tab.value}
                className={`p-4 transition-colors duration-200 ${
                  activeTab === tab.value
                    ? 'bg-[#00A0DF] text-white'
                    : 'hover:bg-gray-100'
                }`}
                onClick={() => {
                  if ((tab.value === 'admin' || tab.value === 'recruiter-signup') && !isAuthenticated) {
                    setActiveTab(tab.value);
                  } else {
                    setActiveTab(tab.value);
                  }
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'map' && (
            <div className={`${darkMode ? 'bg-gray-900' : 'bg-white'}`} style={{ height: 'calc((100vh - 200px) * 1.25)', position: 'relative' }}>
              <MapView
                students={filteredStudents}
                darkMode={darkMode}
                centerZip={centerZip}
                radiusMiles={radiusMiles}
                onStudentSelect={handleStudentSelect}
                zipCodeMap={zipCodeMap}
                areaCodeMap={areaCodeMap}
              />
            </div>
          )}
          {activeTab === 'students' && <StudentProfiles data={filteredStudents} darkMode={darkMode} filters={filters} setFilters={setFilters} shortlist={shortlist} addToShortlist={addToShortlist} removeFromShortlist={removeFromShortlist} />}
          {activeTab === 'analytics' && <Analytics data={filteredStudents} darkMode={darkMode} />}
          {activeTab === 'shortlist' && <Shortlist shortlist={shortlist} removeFromShortlist={removeFromShortlist} darkMode={darkMode} />}
        </main>
      </div>
    </AdminRouteGuard>
  );
};

export default App; 